<template>
  <div class="modal fade" id="bspPriceEditModal" tabindex="-1" aria-labelledby="bspPriceEditModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="bspPriceEditModalLabel">
            <i class="fas fa-edit me-2"></i>{{ $t('product.editBspPrice') }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="product">
            <h6 class="mb-3">{{ product.name }}</h6>
            <div class="mb-3">
              <label for="bspPriceInput" class="form-label">{{ $t('product.bspPrice') }}</label>
              <input
                type="number"
                step="0.01"
                class="form-control"
                id="bspPriceInput"
                v-model.number="editedBspPrice"
                :class="{ 'is-invalid': validationError }"
              />
              <div class="invalid-feedback" v-if="validationError">
                {{ validationError }}
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('product.cancel') }}</button>
          <button type="button" class="btn btn-primary" @click="saveBspPrice" :disabled="saving">
            <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            {{ saving ? $t('product.saving') : $t('product.save') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue';
import { Modal } from 'bootstrap';
import apiClient from '../api'; // Assuming apiClient is in ../api

const props = defineProps({
  product: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['bspPriceUpdated']);

const editedBspPrice = ref(0);
const saving = ref(false);
const validationError = ref('');
let bspPriceModal = null;

watch(() => props.product, (newProduct) => {
  if (newProduct) {
    editedBspPrice.value = newProduct.bsp_price || 0;
    validationError.value = ''; // Clear validation error on product change
  }
}, { immediate: true });

const openModal = () => {
  nextTick(() => {
    if (!bspPriceModal) {
      bspPriceModal = new Modal(document.getElementById('bspPriceEditModal'));
    }
    bspPriceModal.show();
  });
};

const closeModal = () => {
  if (bspPriceModal) {
    bspPriceModal.hide();
  }
};

const validateBspPrice = () => {
  if (typeof editedBspPrice.value !== 'number' || isNaN(editedBspPrice.value)) {
    validationError.value = 'BSP price must be a number.';
    return false;
  }
  if (editedBspPrice.value < 0) {
    validationError.value = 'BSP price cannot be negative.';
    return false;
  }
  // Check for precision (max 2 decimal places)
  const decimalPlaces = (editedBspPrice.value.toString().split('.')[1] || '').length;
  if (decimalPlaces > 2) {
    validationError.value = 'BSP price can have a maximum of 2 decimal places.';
    return false;
  }
  validationError.value = '';
  return true;
};

const saveBspPrice = async () => {
  if (!validateBspPrice()) {
    return;
  }

  if (!props.product) {
    console.error('No product selected for BSP price update.');
    return;
  }

  saving.value = true;
  try {
    const payload = { bsp_price: editedBspPrice.value };
    await apiClient.patch(`/products/${props.product.id}/bsp_price`, payload);
    emit('bspPriceUpdated', { productId: props.product.id, newBspPrice: editedBspPrice.value });
    closeModal();
  } catch (error) {
    console.error('Failed to update BSP price:', error);
    validationError.value = error.response?.data?.detail || error.message || 'Failed to save BSP price.';
  } finally {
    saving.value = false;
  }
};

defineExpose({
  openModal,
});
</script>

<style scoped>
/* Add any specific styles for the modal here if needed */
</style>
